import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { clientSearchesApi } from "core/api";
import {
  formatClientSearchOwner,
  hostOf,
  parseClientSearchError,
} from "core/api/client-searches/client-searches.helpers";
import {
  ClientSearchOwner,
  ClientSearchPracticeCreated,
} from "core/types/client-search.types";
import { NO_ESCAPE, isOwnerSuspended } from "./client-searches.utils";

// Stesso tetto del backend (too_many_urls): fermarsi qui evita un giro a vuoto.
export const MAX_PRACTICE_URLS = 500;

// customer_name finisce in una colonna da 255 caratteri: il backend tronca, qui si
// impedisce di scriverne di piu' cosi' l'operatore vede esattamente cosa va in diffida.
const CUSTOMER_NAME_MAX = 255;

type Props = {
  open: boolean;
  searchId: number;
  owner: ClientSearchOwner;
  // Default del titolare dei diritti: la persona cercata (searches.full_name).
  defaultCustomerName: string;
  // Link selezionati, nell'ordine dei risultati.
  urls: string[];
  onClose: () => void;
  // Pratica creata: i link inviati (e quelli scartati) sono ora in una pratica del
  // cliente, la pagina li blocca.
  onCreated: (created: ClientSearchPracticeCreated, sentUrls: string[]) => void;
  // 409 urls_already_in_practice: erano TUTTI gia' in una pratica del cliente.
  onAlreadyInPractice: (urls: string[]) => void;
  onOpenPractice: (practiceId: number) => void;
};

/**
 * Crea la pratica intestata al PROPRIETARIO della ricerca (non all'operatore).
 * Nessun credito del cliente viene scalato; un cliente sospeso per mancato pagamento si
 * puo' lavorare solo con una conferma esplicita, che il backend registra.
 */
export const CreatePracticeDialog: React.FC<Props> = ({
  open,
  searchId,
  owner,
  defaultCustomerName,
  urls,
  onClose,
  onCreated,
  onAlreadyInPractice,
  onOpenPractice,
}) => {
  const { t } = useTranslation();
  // Lo stato nasce dalle props al montaggio: la pagina rimonta il dialog (key) a ogni
  // apertura, cosi' la conferma su un cliente sospeso NON si eredita da un tentativo
  // precedente.
  const [customerName, setCustomerName] = useState(defaultCustomerName);
  const [confirmSuspended, setConfirmSuspended] = useState(false);
  // Diventa true anche se la pagina credeva il cliente attivo e il backend risponde
  // 409 owner_suspended (stato cambiato nel frattempo): da li' la conferma e' richiesta.
  const [suspendedRequired, setSuspendedRequired] = useState(() =>
    isOwnerSuspended(owner)
  );
  const [suspendedMessage, setSuspendedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Pratica creata ma con link scartati perche' gia' in un'altra pratica del cliente:
  // si resta nel dialog per dirlo, invece di navigare e far sparire l'informazione.
  const [partial, setPartial] = useState<ClientSearchPracticeCreated | null>(null);

  const customer = formatClientSearchOwner(owner) || "—";
  const trimmedName = customerName.trim();
  const tooMany = urls.length > MAX_PRACTICE_URLS;
  const canSubmit =
    !submitting &&
    !partial &&
    urls.length > 0 &&
    !tooMany &&
    !!trimmedName &&
    (!suspendedRequired || confirmSuspended);

  const handleClose = () => {
    // Durante la POST il dialog non si chiude: la risposta (id della pratica o errore)
    // deve arrivare a qualcuno.
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const sentUrls = urls.slice();
    setSubmitting(true);
    setError(null);
    try {
      const created = await clientSearchesApi.createPractice(searchId, {
        urls: sentUrls,
        customerName: trimmedName,
        // Solo con la spunta dell'operatore, mai di default.
        confirmSuspended: suspendedRequired && confirmSuspended ? true : undefined,
      });
      if (!Number.isInteger(created.id) || created.id <= 0) {
        // Un 201 senza id non e' un successo da mostrare: non si saprebbe dove andare.
        setError(t("clientSearches.dialog.error_no_id"));
        return;
      }
      onCreated(created, sentUrls);
      if (created.skippedAlreadyInPractice.length > 0) {
        setPartial(created);
      } else {
        onOpenPractice(created.id);
      }
    } catch (e: any) {
      const err = parseClientSearchError(
        e,
        t("clientSearches.dialog.error_generic", {
          error: e?.message || String(e),
          interpolation: NO_ESCAPE,
        })
      );
      if (err.code === "owner_suspended") {
        // Il messaggio del backend va nell'avviso, accanto alla spunta da mettere.
        setSuspendedRequired(true);
        setConfirmSuspended(false);
        setSuspendedMessage(err.message);
        return;
      }
      if (err.code === "urls_already_in_practice") {
        onAlreadyInPractice(err.urls.length ? err.urls : sentUrls);
      }
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("clientSearches.dialog.title", {
          customer,
          interpolation: NO_ESCAPE,
        })}
      </DialogTitle>
      <DialogContent>
        {partial ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t("clientSearches.dialog.partial", {
              id: partial.id,
              urls: partial.urls,
              count: partial.skippedAlreadyInPractice.length,
            })}
          </Alert>
        ) : (
          <>
            <TextField
              autoFocus
              fullWidth
              required
              margin="dense"
              label={t("clientSearches.dialog.rights_holder_label")}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              error={!trimmedName}
              helperText={
                trimmedName
                  ? t("clientSearches.dialog.rights_holder_helper")
                  : t("clientSearches.dialog.rights_holder_required")
              }
              inputProps={{ maxLength: CUSTOMER_NAME_MAX }}
              disabled={submitting}
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {t("clientSearches.dialog.summary", {
                  count: urls.length,
                  customer,
                  interpolation: NO_ESCAPE,
                })}
              </Typography>
              {owner.email && owner.email !== customer && (
                <Typography variant="body2" color="text.secondary">
                  {owner.email}
                </Typography>
              )}
              {/* I link che partono, nell'ordine dei risultati. La selezione sopravvive ai
                  filtri della pagina: qui l'operatore rivede anche quelli che in tabella
                  non ha piu' davanti, prima che entrino in una diffida. */}
              {urls.length > 0 && (
                <Box
                  component="ul"
                  aria-label={t("clientSearches.dialog.urls_label")}
                  sx={{
                    listStyle: "none",
                    m: 0,
                    mt: 1,
                    p: 0,
                    maxHeight: 220,
                    overflowY: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  {urls.map((url, i) => (
                    <Box
                      component="li"
                      key={`${i}:${url}`}
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                        px: 1.5,
                        py: 0.5,
                        borderTop: i ? 1 : 0,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        sx={{ flexShrink: 0, maxWidth: "40%" }}
                      >
                        {hostOf(url) || "—"}
                      </Typography>
                      {/* Troncato a una riga; l'URL intero sta nel title. */}
                      <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                        noWrap
                        title={url}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {url}
                      </Link>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {tooMany && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t("clientSearches.dialog.too_many", { max: MAX_PRACTICE_URLS })}
              </Alert>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              {t("clientSearches.dialog.no_credits")}
            </Alert>

            {suspendedRequired && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {suspendedMessage || t("clientSearches.dialog.suspended_warning")}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmSuspended}
                      onChange={(e) => setConfirmSuspended(e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label={t("clientSearches.dialog.confirm_suspended")}
                />
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {partial ? (
          <>
            <Button onClick={handleClose}>{t("clientSearches.dialog.close")}</Button>
            <Button variant="contained" onClick={() => onOpenPractice(partial.id)}>
              {t("clientSearches.dialog.open_practice")}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              {t("clientSearches.dialog.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              startIcon={submitting ? <CircularProgress size={16} /> : undefined}
            >
              {t("clientSearches.dialog.confirm")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreatePracticeDialog;
